import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';

import { toAuthError } from './errors';
import { MemoryStorage } from './memory-storage';
import type { AuthConfig, AuthTokens, SignInResult } from './types';

const toTokens = (session: CognitoUserSession): AuthTokens => ({
  idToken: session.getIdToken().getJwtToken(),
  accessToken: session.getAccessToken().getJwtToken(),
  refreshToken: session.getRefreshToken().getToken(),
  expiresAt: session.getIdToken().getExpiration(),
});

/**
 * Promise-based wrapper over Cognito's SRP flows for ClassGO's own screens (ADR-0004). One
 * instance per app client; tokens live in memory.
 */
export class CognitoAuth {
  private readonly pool: CognitoUserPool;
  private readonly storage: ICognitoStorage;

  constructor(config: AuthConfig, storage: ICognitoStorage = new MemoryStorage()) {
    this.storage = storage;
    this.pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.clientId,
      Storage: storage,
      ...(config.endpoint ? { endpoint: config.endpoint } : {}),
    });
  }

  private user(email: string): CognitoUser {
    const user = new CognitoUser({ Username: email, Pool: this.pool, Storage: this.storage });
    user.setAuthenticationFlowType('USER_SRP_AUTH');
    return user;
  }

  private resultCallbacks(
    user: CognitoUser,
    resolve: (r: SignInResult) => void,
    reject: (e: unknown) => void,
  ) {
    return {
      onSuccess: (session: CognitoUserSession) =>
        resolve({ kind: 'signedIn', tokens: toTokens(session) }),
      onFailure: (error: unknown) => reject(toAuthError(error)),
      newPasswordRequired: () =>
        resolve({
          kind: 'newPasswordRequired',
          continuation: {
            complete: (newPassword) =>
              new Promise<SignInResult>((res, rej) =>
                user.completeNewPasswordChallenge(
                  newPassword,
                  {},
                  this.resultCallbacks(user, res, rej),
                ),
              ),
          },
        }),
      totpRequired: () =>
        resolve({
          kind: 'totpRequired',
          continuation: {
            submit: (code) =>
              new Promise<SignInResult>((res, rej) =>
                user.sendMFACode(code, this.resultCallbacks(user, res, rej), 'SOFTWARE_TOKEN_MFA'),
              ),
          },
        }),
      mfaSetup: () =>
        resolve({
          kind: 'totpSetupRequired',
          continuation: {
            begin: () =>
              new Promise<{ secret: string }>((res, rej) =>
                user.associateSoftwareToken({
                  associateSecretCode: (secret: string) => res({ secret }),
                  onFailure: (error: unknown) => rej(toAuthError(error)),
                }),
              ),
            verify: (code) =>
              new Promise<SignInResult>((res, rej) =>
                user.verifySoftwareToken(code, 'ClassGO', {
                  onSuccess: (session: CognitoUserSession) =>
                    res({ kind: 'signedIn', tokens: toTokens(session) }),
                  onFailure: (error: unknown) => rej(toAuthError(error)),
                }),
              ),
          },
        }),
    };
  }

  /**
   * Starts a sign-in with email and password. The result tells the screen what comes next.
   *
   * @param {string} email -- The user's email.
   * @param {string} password -- The password, temporary or permanent.
   * @returns The next step or the tokens.
   * @throws {AuthError} With a stable code when Cognito refuses.
   */
  signIn(email: string, password: string): Promise<SignInResult> {
    const user = this.user(email);
    return new Promise((resolve, reject) =>
      user.authenticateUser(
        new AuthenticationDetails({ Username: email, Password: password }),
        this.resultCallbacks(user, resolve, reject),
      ),
    );
  }

  /**
   * Sends the password-reset code to the user's email.
   *
   * @param {string} email -- The user's email.
   */
  forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) =>
      this.user(email).forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (error: unknown) => reject(toAuthError(error)),
        inputVerificationCode: () => resolve(),
      }),
    );
  }

  /**
   * Sets a new password with the emailed code.
   *
   * @param {string} email -- The user's email.
   * @param {string} code -- The verification code from the email.
   * @param {string} newPassword -- The new permanent password.
   */
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) =>
      this.user(email).confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (error: unknown) => reject(toAuthError(error)),
      }),
    );
  }

  /**
   * Exchanges a refresh token for fresh tokens, used on reload and before expiry.
   *
   * @param {string} email -- The user's email.
   * @param {string} refreshToken -- The refresh token from a previous sign-in.
   * @returns Fresh tokens.
   */
  refresh(email: string, refreshToken: string): Promise<AuthTokens> {
    return new Promise((resolve, reject) =>
      this.user(email).refreshSession(
        new CognitoRefreshToken({ RefreshToken: refreshToken }),
        (error: unknown, session: CognitoUserSession) => {
          if (error) {
            reject(toAuthError(error));
          } else {
            resolve(toTokens(session));
          }
        },
      ),
    );
  }

  private signedInUser(email: string): Promise<CognitoUser> {
    const user = this.user(email);
    return new Promise((resolve, reject) =>
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        if (error || !session?.isValid()) {
          reject(toAuthError(error ?? { code: 'NotAuthorizedException' }));
        } else {
          resolve(user);
        }
      }),
    );
  }

  /**
   * Starts TOTP enrollment for the signed-in person (ticket #54): Cognito hands back the secret
   * the authenticator app needs.
   *
   * @param {string} email -- The signed-in user's email.
   * @returns The secret to show as text and as an `otpauth://` QR.
   * @throws {AuthError} When there is no valid session.
   */
  async beginTotpEnrollment(email: string): Promise<{ secret: string }> {
    const user = await this.signedInUser(email);
    return new Promise((resolve, reject) =>
      user.associateSoftwareToken({
        associateSecretCode: (secret: string) => resolve({ secret }),
        onFailure: (error: unknown) => reject(toAuthError(error)),
      }),
    );
  }

  /**
   * Verifies the first code from the authenticator and makes TOTP the user's preferred and
   * enabled factor, so Cognito asks for it on every sign-in from now on.
   *
   * @param {string} email -- The signed-in user's email.
   * @param {string} code -- The six-digit code.
   * @throws {AuthError} `CODE_MISMATCH` when the code is wrong.
   */
  async completeTotpEnrollment(email: string, code: string): Promise<void> {
    const user = await this.signedInUser(email);
    await new Promise<void>((resolve, reject) =>
      user.verifySoftwareToken(code, 'ClassGO', {
        onSuccess: () => resolve(),
        onFailure: (error: unknown) => reject(toAuthError(error)),
      }),
    );
    await new Promise<void>((resolve, reject) =>
      user.setUserMfaPreference(
        null,
        { PreferredMfa: true, Enabled: true },
        (error: Error | undefined) => (error ? reject(toAuthError(error)) : resolve()),
      ),
    );
  }

  /**
   * Forgets everything this instance cached. The API's `POST /auth/logout` revokes the session
   * server-side.
   */
  signOutLocal(): void {
    this.storage.clear();
  }
}
